/*
The API key for your Amplitude project is required. Optionally, a user ID and config object can be passed in this call.
The SDK can be used anywhere after it is initialized anywhere in an application.

Amplitude is a "statistics" cookie under Cookiebot, so nothing here runs until
statistics consent is granted — see utils/helpers/cookiebot.
*/

import { init, add, setOptOut, reset } from '@amplitude/analytics-browser';
import { removeQueryParameter } from 'utils/urlSearchParams';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import { hasStatisticsConsent } from 'utils/helpers/cookiebot';

const DEVICE_ID_STORAGE_KEY = 'amplitudeDeviceId';

// The SDK stays loaded for the life of the page once initialised, so a revoke
// has to opt out rather than unload, and a re-grant has to opt back in.
let hasInitialized = false;

const getDeviceId = () => {
  const storedDeviceId = sessionStorage.getItem(DEVICE_ID_STORAGE_KEY);
  if (storedDeviceId !== null) {
    return storedDeviceId;
  }
  const deviceId = new URLSearchParams(window.location.search).get('deviceId');
  if (deviceId === null) {
    return undefined;
  }
  sessionStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
  removeQueryParameter('deviceId');
  return deviceId;
};

const addSessionReplay = () => {
  if (process.env.REACT_APP_ENV === 'prod' || process.env.REACT_APP_ENV === 'uat') {
    const sessionReplayTracking = sessionReplayPlugin({ sampleRate: 1 });
    add(sessionReplayTracking);
  }
};

const initAmplitude = async (showAmplitudeSurvey: boolean, currentLocale?: string) => {
  if (!hasStatisticsConsent()) {
    // Statistics cookies not accepted: never load the SDK or send an event.
    console.log('Amplitude not initialized: statistics consent not granted');
    return false;
  }
  if (!process.env.REACT_APP_AMPLITUDE_API_KEY) {
    console.log('Amplitude not initialized: REACT_APP_AMPLITUDE_API_KEY is missing');
    return false;
  }
  console.log('Initializing Amplitude...');
  const data = init(`${process.env.REACT_APP_AMPLITUDE_API_KEY}`, undefined, {
    deviceId: getDeviceId(),
    defaultTracking: {
      pageViews: true,
      sessions: true,
    },
    // No remote config is published for these projects, so don't let init()
    // block on fetching it. Without this, events tracked right after consent
    // (e.g. 'DDG rec opens') sit in the queue for seconds while init awaits
    // the remote-config round trip, and are lost if the tab closes first.
    fetchRemoteConfig: false,
  });
  // Clears a previous revoke, so consent granted again in the same page load
  // starts sending rather than silently staying opted out.
  setOptOut(false);
  hasInitialized = true;
  if (showAmplitudeSurvey) {
    add(engagementPlugin({ locale: currentLocale }));
  }
  addSessionReplay();
  data?.promise
    ?.then(() => {
      console.log('Amplitude initialized — SDK ready');
    })
    .catch((error) => {
      console.error('Amplitude initialization failed', error);
    });
  return data;
};

/*
Stops Amplitude sending anything after consent is declined or revoked, and drops
the identifiers it was using. Safe to call when Amplitude was never initialised.
*/
const disableAmplitudeTracking = (): boolean => {
  sessionStorage.removeItem(DEVICE_ID_STORAGE_KEY);
  if (!hasInitialized) {
    return false;
  }
  console.log('Amplitude tracking disabled (statistics consent withdrawn)');
  setOptOut(true);
  reset();
  return true;
};

export { initAmplitude, disableAmplitudeTracking };
